<i18n locale="en">
{
  "callouts": {
    "fast-ordering": {
      "body": "Regulars can reorder in seconds.",
      "title": "Fast reordering"
    },
    "notifications": {
      "body": "Reach customers at the right moment.",
      "title": "Perfectly timed notifications"
    },
    "rewards": {
      "body": "Turns regulars into repeat buyers.",
      "title": "Customers earn rewards"
    }
  }
}
</i18n>
<template>
  <div
    class="upsell-hero flex w-full flex-col items-center gap-4 text-center"
    data-testid="upsell-hero"
  >
    <OwnType
      class="upsell-hero__title"
      :text="title"
      variant="heading-regular"
      align="center"
    />
    <OwnType
      v-if="description"
      class="upsell-hero__description"
      :text="description"
      variant="paragraph-regular"
      align="center"
    />
    <div class="upsell-hero__preview" data-testid="upsell-hero-preview">
      <div class="upsell-hero__preview-background" />
      <PersonalizedMobileAppPreview
        compact
        :data="previewData ?? fallbackPreviewData"
        :show-callouts="false"
        test-id="upsell-hero-phone"
      />
      <div
        class="upsell-hero__callout upsell-hero__callout--fast-ordering"
        data-testid="upsell-hero-callout-fast-ordering"
      >
        <Tag :size="20" data-testid="upsell-hero-callout-fast-ordering-icon" />
        <div>
          <strong>{{ t('callouts.fast-ordering.title') }}</strong>
          <span>{{ t('callouts.fast-ordering.body') }}</span>
        </div>
      </div>
      <div
        class="upsell-hero__callout upsell-hero__callout--rewards"
        data-testid="upsell-hero-callout-rewards"
      >
        <Medal :size="20" data-testid="upsell-hero-callout-rewards-icon" />
        <div>
          <strong>{{ t('callouts.rewards.title') }}</strong>
          <span>{{ t('callouts.rewards.body') }}</span>
        </div>
      </div>
      <div
        class="upsell-hero__callout upsell-hero__callout--notifications"
        data-testid="upsell-hero-callout-notifications"
      >
        <BubbleWideNotification
          :size="20"
          data-testid="upsell-hero-callout-notifications-icon"
        />
        <div>
          <strong>{{ t('callouts.notifications.title') }}</strong>
          <span>{{ t('callouts.notifications.body') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BubbleWideNotification } from '@owner/icons-vue/BubbleWideNotification'
import { Medal } from '@owner/icons-vue/Medal'
import { Tag } from '@owner/icons-vue/Tag'
import { useI18n } from 'vue-i18n'

import { OwnType } from '@/ui/OwnType'

import fallbackMobileHero from '../assets/fallback-mobile-hero.png'
import type { PersonalizedProUpsellPreviewData } from '../composables/usePersonalizedProUpsellPreview'

import PersonalizedMobileAppPreview from './PersonalizedMobileAppPreview.vue'

const { t } = useI18n()

const fallbackPreviewData: PersonalizedProUpsellPreviewData = {
  address: '4901 Telegraph Ave, Oakland, CA',
  appIconBackgroundColor: 'white',
  appIconUrl: null,
  brandName: 'Hawking Bird',
  heroImageUrl: fallbackMobileHero,
  locationName: 'Hawking Bird',
  statusLabel: 'Open until 9 PM',
}

withDefaults(
  defineProps<{
    description?: string
    previewData?: PersonalizedProUpsellPreviewData | null
    title: string
  }>(),
  {
    description: undefined,
    previewData: null,
  }
)
</script>

<style lang="scss" scoped>
@use '@/styles/breakpoints';

// Light hero from the tier-upgrades design (Figma node 806:46646): title and
// subtitle stacked over the hero-phone artwork.
.upsell-hero {
  &__title {
    max-width: 600px;
    font-weight: 500;
  }

  &__preview {
    position: relative;
    width: 100%;
    max-width: 900px;
    height: 359px;
    overflow: hidden;
    container-name: upsell-hero-preview;
    container-type: inline-size;
  }

  &__preview-background {
    position: absolute;
    top: 95px;
    right: 0;
    left: 0;
    height: 264px;
    border-radius: 20px;
    background: url('../assets/hero-gate-background.png') center / cover;
  }

  :deep(.personalized-mobile-app-preview) {
    position: absolute;
    inset: 0;
    z-index: 1;
    width: 100%;
    max-width: none;
    height: 359px;
  }

  :deep(.personalized-mobile-app-preview__scale) {
    max-width: none;
    height: 359px;
  }

  :deep(.personalized-mobile-app-preview__phone) {
    transform: translateX(-50%) scale(0.74);
  }

  :deep(.personalized-mobile-app-preview__bottom-fade) {
    display: none;
  }

  &__callout {
    position: absolute;
    z-index: 2;
    display: flex;
    gap: 12px;
    align-items: center;
    width: 248px;
    min-height: 64px;
    padding: 12px 16px;
    border: 1px solid rgb(255 255 255 / 48%);
    border-radius: 12px;
    background: rgb(255 255 255 / 80%);
    box-shadow: 0 8px 18px rgb(45 72 135 / 12%);
    color: #272a31;
    text-align: left;
    backdrop-filter: blur(10px);

    :deep(svg) {
      flex: none;
      color: #454c87;
    }

    > div {
      display: flex;
      flex-direction: column;
      gap: 1px;
    }

    strong {
      font-size: 13px;
      font-weight: 500;
      line-height: 18px;
    }

    span {
      color: #0d0e0e;
      font-size: 11px;
      line-height: 16px;
    }

    &--fast-ordering {
      top: 190px;
      left: 10%;
      z-index: 0;
      width: 260px;
    }

    &--rewards {
      top: 130px;
      right: 15%;
      width: 240px;
    }

    &--notifications {
      top: 270px;
      right: 8%;
      width: 260px;
    }
  }

  @container upsell-hero-preview (max-width: 813px) {
    &__callout {
      display: none;
    }
  }

  @include breakpoints.respond-to('sm-and-down') {
    &__preview {
      height: 255px;
    }

    &__preview-background {
      top: 75px;
      right: 0;
      left: 0;
      height: 180px;
    }

    :deep(.personalized-mobile-app-preview) {
      height: 255px;
    }

    :deep(.personalized-mobile-app-preview__scale) {
      height: 255px;
    }

    :deep(.personalized-mobile-app-preview__phone) {
      transform: translateX(-50%) scale(0.58);
    }
  }

  &__description {
    max-width: 580px;
  }
}
</style>
